import {
  CheckCircle,
  ExpandLess,
  ExpandMore,
  LayersOutlined,
  LowPriority,
  MenuBookOutlined,
  ScheduleOutlined,
  School,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import { ActivityRowHeader } from "../components/ActivityRowHeader";
import { CustomConfirm } from "../components/CustomConfirm";
import {
  Eyebrow,
  grantedStyle,
  itemCardSx,
  ItemDescription,
  ItemThumbnail,
  ListMessage,
  matchesSearch,
  metaChipSx,
  MetadataChips,
  metaIconSx,
  MetaItem,
  MetaRow,
  panelSx,
} from "../components/learning/contentCard";
import {
  LearningGrantConflictError,
  OrganizationLearningCourse,
  OrganizationLearningPath,
  useOrganizationLearningCatalog,
} from "../hooks/useOrganizationLearningCatalog";
import { borderRadius, color, typography } from "../theme/designTokens";

type GrantTarget = {
  id: string;
  itemKind: "course" | "learningPath";
  title: string;
};

// "3 von 8 freigeschaltet" per content kind, so the state of the whole catalog
// is readable without scrolling through it.
const SummaryTile = ({ label, granted, total }: { label: string; granted: number; total: number }) => {
  const translate = useTranslate();
  return (
    <Box
      sx={{
        px: 1.75,
        py: 1,
        minWidth: 172,
        border: `1px solid ${color.panelBorder}`,
        borderRadius: borderRadius.sm,
        backgroundColor: color.background2,
      }}
    >
      <Eyebrow>{label}</Eyebrow>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flex: "0 0 8px",
            backgroundColor: granted > 0 ? grantedStyle.color : color.secondary40,
          }}
        />
        <Typography
          variant="body2"
          sx={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold }}
        >
          {translate("learningGrants.summary.grantedOfTotal", { granted, total })}
        </Typography>
      </Box>
    </Box>
  );
};

const GrantButton = ({
  granted,
  itemId,
  itemTitle,
  loading,
  onChange,
}: {
  granted: boolean;
  itemId: string;
  itemTitle: string;
  loading: boolean;
  onChange: (granted: boolean) => void;
}) => {
  const translate = useTranslate();
  return (
    <Button
      variant={granted ? "outlined" : "contained"}
      startIcon={loading ? <CircularProgress size={16} /> : granted ? <CheckCircle /> : undefined}
      disabled={loading}
      onClick={() => onChange(!granted)}
      aria-label={translate(granted ? "learningGrants.actions.revokeLabel" : "learningGrants.actions.grantLabel", {
        title: itemTitle,
      })}
      data-testid={`learning-grant-${itemId}`}
      sx={{
        flex: "0 0 auto",
        minWidth: 150,
        // Granted reads as a state, and hovering it hints that clicking revokes.
        ...(granted && {
          backgroundColor: color.white,
          borderColor: grantedStyle.border,
          color: grantedStyle.color,
          "&:hover": { backgroundColor: color.white, borderColor: color.error, color: color.error },
        }),
      }}
    >
      {translate(granted ? "learningGrants.status.granted" : "learningGrants.actions.grant")}
    </Button>
  );
};

const CourseCards = ({
  courses,
  emptyMessage,
  pendingItemIds,
  onChange,
}: {
  courses: OrganizationLearningCourse[];
  emptyMessage: string;
  pendingItemIds: Set<string>;
  onChange: (course: OrganizationLearningCourse, granted: boolean) => void;
}) => {
  const translate = useTranslate();
  if (courses.length === 0) return <ListMessage message={emptyMessage} />;

  return (
    <>
      {courses.map(course => (
        <Box key={course.id} sx={itemCardSx(course.granted)} data-testid={`learning-course-${course.id}`}>
          <ActivityRowHeader
            gap={2}
            leading={<ItemThumbnail thumbnailFilename={course.thumbnailFilename} />}
            title={course.title}
            caption={
              <>
                <ItemDescription description={course.description} />
                <MetaRow>
                  <MetaItem
                    icon={<MenuBookOutlined sx={metaIconSx} />}
                    label={translate("learningGrants.values.lessons", { smart_count: course.lessonCount })}
                  />
                  <MetaItem
                    icon={<ScheduleOutlined sx={metaIconSx} />}
                    label={translate("learningGrants.values.duration", { minutes: course.estimatedDurationMinutes })}
                  />
                  <MetadataChips tags={course.tags} />
                </MetaRow>
              </>
            }
            trailing={
              <GrantButton
                granted={course.granted}
                itemId={course.id}
                itemTitle={course.title}
                loading={pendingItemIds.has(course.id)}
                onChange={granted => onChange(course, granted)}
              />
            }
          />
        </Box>
      ))}
    </>
  );
};

const PathCards = ({
  learningPaths,
  coursesById,
  emptyMessage,
  pendingItemIds,
  onChange,
}: {
  learningPaths: OrganizationLearningPath[];
  coursesById: Map<string, OrganizationLearningCourse>;
  emptyMessage: string;
  pendingItemIds: Set<string>;
  onChange: (path: OrganizationLearningPath, granted: boolean) => void;
}) => {
  const translate = useTranslate();
  const [expandedPathIds, setExpandedPathIds] = useState<string[]>([]);
  if (learningPaths.length === 0) return <ListMessage message={emptyMessage} />;

  const toggleExpanded = (pathId: string) => {
    setExpandedPathIds(previous =>
      previous.includes(pathId) ? previous.filter(id => id !== pathId) : [...previous, pathId]
    );
  };

  return (
    <>
      {learningPaths.map(path => {
        const expanded = expandedPathIds.includes(path.id);
        const memberCourses = [...path.courses]
          .sort((left, right) => left.position - right.position)
          .map(member => coursesById.get(member.courseId))
          .filter((course): course is OrganizationLearningCourse => course != null);

        return (
          <Box key={path.id} sx={itemCardSx(path.granted)} data-testid={`learning-path-${path.id}`}>
            <ActivityRowHeader
              gap={2}
              leading={<ItemThumbnail thumbnailFilename={path.thumbnailFilename} />}
              title={path.title}
              caption={
                <>
                  <ItemDescription description={path.description} />
                  <MetaRow>
                    <MetaItem
                      icon={<LayersOutlined sx={metaIconSx} />}
                      label={translate("learningGrants.values.courses", { smart_count: path.courses.length })}
                    />
                    <MetaItem
                      icon={<ScheduleOutlined sx={metaIconSx} />}
                      label={translate("learningGrants.values.duration", { minutes: path.estimatedDurationMinutes })}
                    />
                    {path.enforceOrder && (
                      <Chip
                        icon={<LowPriority sx={{ fontSize: "15px !important" }} />}
                        label={translate("learningGrants.enforceOrder")}
                        size="small"
                        sx={metaChipSx}
                      />
                    )}
                    <MetadataChips tags={path.tags} />
                  </MetaRow>
                </>
              }
              trailing={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <GrantButton
                    granted={path.granted}
                    itemId={path.id}
                    itemTitle={path.title}
                    loading={pendingItemIds.has(path.id)}
                    onChange={granted => onChange(path, granted)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => toggleExpanded(path.id)}
                    aria-label={translate(
                      expanded ? "learningGrants.actions.collapse" : "learningGrants.actions.expand",
                      {
                        title: path.title,
                      }
                    )}
                  >
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
              }
            />
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px dashed ${color.panelBorder}` }}>
                <Typography variant="body2" sx={{ fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                  {translate("learningGrants.pathCourses")}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {memberCourses.map((course, index) => (
                    <Box key={course.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          display: "grid",
                          placeItems: "center",
                          flex: "0 0 22px",
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          backgroundColor: color.background2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: typography.fontSize.xxs,
                            fontWeight: typography.fontWeight.semibold,
                            color: "text.secondary",
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </Box>
                      <Typography variant="body2">{course.title}</Typography>
                      {course.granted && <CheckCircle sx={{ fontSize: 15, color: grantedStyle.color }} />}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </>
  );
};

type OrganizationLearningGrantsPageProps = {
  organizationId: string;
  organizationName: string;
};

export const OrganizationLearningGrantsPage = ({
  organizationId,
  organizationName,
}: OrganizationLearningGrantsPageProps) => {
  const catalog = useOrganizationLearningCatalog(organizationId);
  return <OrganizationLearningGrantsContent organizationName={organizationName} catalog={catalog} />;
};

export const OrganizationLearningGrantsContent = ({
  organizationName,
  catalog,
}: {
  organizationName: string;
  catalog: ReturnType<typeof useOrganizationLearningCatalog>;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const {
    courses,
    learningPaths,
    loading,
    error,
    isEmpty,
    pendingItemIds,
    reload,
    setCourseGrant,
    setLearningPathGrant,
  } = catalog;
  const [tab, setTab] = useState<"courses" | "paths">("courses");
  const [search, setSearch] = useState("");
  const [revokeTarget, setRevokeTarget] = useState<GrantTarget | null>(null);

  const filteredCourses = useMemo(
    () => courses.filter(course => matchesSearch(search, [course.title, course.description, ...course.tags])),
    [courses, search]
  );
  const filteredPaths = useMemo(
    () => learningPaths.filter(path => matchesSearch(search, [path.title, path.description, ...path.tags])),
    [learningPaths, search]
  );
  const coursesById = useMemo(() => new Map(courses.map(course => [course.id, course])), [courses]);
  const grantedCourseCount = useMemo(() => courses.filter(course => course.granted).length, [courses]);
  const grantedPathCount = useMemo(() => learningPaths.filter(path => path.granted).length, [learningPaths]);

  const updateGrant = async (target: GrantTarget, granted: boolean): Promise<boolean> => {
    try {
      if (target.itemKind === "course") await setCourseGrant(target.id, granted);
      else await setLearningPathGrant(target.id, granted);
      notify(
        translate(granted ? "learningGrants.notifications.granted" : "learningGrants.notifications.revoked", {
          title: target.title,
        }),
        { type: "success" }
      );
      return true;
    } catch (updateError) {
      notify(
        translate(
          updateError instanceof LearningGrantConflictError
            ? "learningGrants.notifications.conflict"
            : "learningGrants.notifications.error"
        ),
        { type: "error" }
      );
      return false;
    }
  };

  const requestChange = (target: GrantTarget, granted: boolean) => {
    if (granted) void updateGrant(target, true);
    else setRevokeTarget(target);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    if (await updateGrant(revokeTarget, false)) setRevokeTarget(null);
  };

  const hasCatalog = courses.length > 0 || learningPaths.length > 0;
  const emptyMessage = search.trim()
    ? translate("learningGrants.noResults")
    : translate(tab === "courses" ? "learningGrants.noCoursesInCatalog" : "learningGrants.noPathsInCatalog");

  return (
    <Box data-testid="organization-learning-grants-page">
      {/* Context bar: which facility these grants apply to, plus how much of the
          catalog is already unlocked — both above the fold. */}
      <Box
        data-testid="learning-grants-organization-context"
        sx={{ ...panelSx, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, px: 2.5, py: 1.75, mb: 2 }}
      >
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            flex: "0 0 44px",
            width: 44,
            height: 44,
            borderRadius: "50%",
            backgroundColor: color.secondaryButtonBackground,
          }}
        >
          <School sx={{ fontSize: 24, color: color.primary }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Eyebrow>{translate("learningGrants.organizationContext.label")}</Eyebrow>
          <Typography variant="h6" sx={{ lineHeight: typography.lineHeight.xs }}>
            {organizationName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: typography.fontSize.xs, mt: 0.25 }}>
            {translate("learningGrants.organizationContext.description")}
          </Typography>
        </Box>
        {hasCatalog && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <SummaryTile
              label={translate("learningGrants.tabs.courses")}
              granted={grantedCourseCount}
              total={courses.length}
            />
            <SummaryTile
              label={translate("learningGrants.tabs.paths")}
              granted={grantedPathCount}
              total={learningPaths.length}
            />
          </Box>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: borderRadius.sm }}
          action={
            <Button color="inherit" onClick={() => void reload()}>
              {translate("learningGrants.actions.retry")}
            </Button>
          }
        >
          {translate("learningGrants.loadError")}
        </Alert>
      )}

      {isEmpty ? (
        <Box sx={{ ...panelSx, px: 3, py: 5, textAlign: "center" }} data-testid="learning-grants-empty">
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 56,
              height: 56,
              mx: "auto",
              mb: 1.5,
              borderRadius: "50%",
              backgroundColor: color.background2,
            }}
          >
            <School sx={{ fontSize: 28, color: color.iconMuted }} />
          </Box>
          <Typography variant="h6">{translate("learningGrants.emptyTitle")}</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5, mx: "auto", maxWidth: 460 }}>
            {translate("learningGrants.emptyDescription")}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ ...panelSx, overflow: "hidden" }}>
          <Box
            sx={{
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              borderBottom: `1px solid ${color.panelBorder}`,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_event, value) => setTab(value)}
              aria-label={translate("learningGrants.tabs.label")}
              sx={{
                minHeight: 48,
                "& .MuiTab-root": {
                  minHeight: 48,
                  px: 1.5,
                  textTransform: "none",
                  fontSize: typography.fontSize.md,
                  fontWeight: typography.fontWeight.medium,
                },
              }}
            >
              <Tab value="courses" label={translate("learningGrants.tabs.courses")} />
              <Tab value="paths" label={translate("learningGrants.tabs.paths")} />
            </Tabs>
            <TextField
              size="small"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={translate("learningGrants.searchPlaceholder")}
              inputProps={{ "aria-label": translate("learningGrants.searchLabel") }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: color.gray }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                my: 1,
                minWidth: 260,
                "& .MuiOutlinedInput-root": {
                  borderRadius: borderRadius.lg,
                  backgroundColor: color.background2,
                  "& fieldset": { borderColor: color.panelBorder },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              backgroundColor: color.background2,
            }}
          >
            {loading ? (
              <Box
                role="status"
                aria-label={translate("learningGrants.loading")}
                sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
              >
                {[0, 1, 2].map(placeholder => (
                  <Skeleton key={placeholder} variant="rounded" height={104} sx={{ borderRadius: borderRadius.sm }} />
                ))}
              </Box>
            ) : tab === "courses" ? (
              <CourseCards
                courses={filteredCourses}
                emptyMessage={emptyMessage}
                pendingItemIds={pendingItemIds}
                onChange={(course, granted) =>
                  requestChange({ id: course.id, itemKind: "course", title: course.title }, granted)
                }
              />
            ) : (
              <PathCards
                learningPaths={filteredPaths}
                coursesById={coursesById}
                emptyMessage={emptyMessage}
                pendingItemIds={pendingItemIds}
                onChange={(path, granted) =>
                  requestChange({ id: path.id, itemKind: "learningPath", title: path.title }, granted)
                }
              />
            )}
          </Box>
        </Box>
      )}

      <CustomConfirm
        isOpen={revokeTarget != null}
        title={translate("learningGrants.revoke.title")}
        content={translate("learningGrants.revoke.content", { title: revokeTarget?.title ?? "" })}
        loading={revokeTarget ? pendingItemIds.has(revokeTarget.id) : false}
        onConfirm={() => void confirmRevoke()}
        onClose={() => setRevokeTarget(null)}
      />
    </Box>
  );
};
